/** @format */

import { Injectable } from "@angular/core";
import { NsWISConfigurations } from "../models/configurations.model";
import { BehaviorSubject, Observable, ReplaySubject, Subject } from "rxjs";
import { NsEmpWebExpData } from "../models/emp-web-exp.model";
import { ApiService } from "./api.service";
import { parseZone } from "moment";
import { EmpUserService } from "./emp-user.service";
import { FormControl } from "@angular/forms";
import { ICommuteChoice } from "../components/forms/commute/commute.component";
import { ConfigurationService } from "./configuration.service";
import { CookieService } from "./cookie.service";

type IFormData = [NsEmpWebExpData.ICommuteOption[], NsEmpWebExpData.ICommuteOption[], ICommute?];
interface ICommute {
  login?: NsEmpWebExpData.ICommuteOption;
  logout?: NsEmpWebExpData.ICommuteOption;
  registrationNumber: string;
}

@Injectable({
  providedIn: "root"
})
export class CommuteService {
  private formData$ = new ReplaySubject<IFormData>(1);
  formData = this.formData$.asObservable();
  public commuteDate = new BehaviorSubject<any>(null);
  private commuteChange$ = new BehaviorSubject<[NsEmpWebExpData.ICommuteOption?, NsEmpWebExpData.ICommuteOption?, string?, string?, string?]>([]);
  commuteChange = this.commuteChange$.asObservable();
  public parkingPremises = new BehaviorSubject<any>(null);
  private removeCommute$ = new Subject<string>();
  removeCommute = this.removeCommute$.asObservable();
  loginOptions: NsEmpWebExpData.ICommuteOption[] = [];
  logoutOptions: NsEmpWebExpData.ICommuteOption[] = [];
  private parkingRefreshStream$ = new ReplaySubject<Observable<NsEmpWebExpData.IParkingPremise[]>>(1);
  parkingRefreshStream = this.parkingRefreshStream$.asObservable();
  regNo = new FormControl("", []);
  temporaryCommuteSelection: [NsEmpWebExpData.ICommuteOption?, NsEmpWebExpData.ICommuteOption?, string?, string?, string?] = [];
  currentChoice: ICommuteChoice;
  shiftsValid = true;
  gridUrlData$ = new BehaviorSubject<any>(null);
  gridUrlData = this.gridUrlData$.asObservable();
  vehicleType$ = new BehaviorSubject<string>(null);
  vehicleType = this.vehicleType$.asObservable();
  allotedParkingDetails$ = new BehaviorSubject<NsEmpWebExpData.IAllotedParkingDetails>(null);
  allotedParkingDetails = this.allotedParkingDetails$.asObservable();
  enableGridFloorPlan = false;
  disableParkingSelection = false;
  multipleDaysSelected = false;
  entityTypeId: string = "";
  entityType: string = "";
  vehicleId: string = "";
  selectedVehicleRegNo: string = "";
  parkingFormOfficeGuid: string;
  parkingFormStartTime: string;
  parkingFormEndTime: string;

  removeParking = new Subject<boolean>();
  constructor(private apiSvc: ApiService, private userSvc: EmpUserService, private configSvc: ConfigurationService, private cookies: CookieService) {}

  async setFormData(
    profile: NsEmpWebExpData.IEmployee,
    configs: NsWISConfigurations.IConfigurations,
    formData: any,
    date: number,
    endDate: number,
    tz: string,
    oldCommute?: ICommute,
    booking?: NsEmpWebExpData.IBookingDetailDTOS,
    multipleDays?: boolean
  ): Promise<void> {
    this.commuteDate.next({ startTime: formData.startDate, endTime: formData.recurrence.endDate ? formData.recurrence.endDate : formData.endDate });
    this.disableParkingSelection = booking?.requestStatus === "SIGNED_IN";
    this.shiftsValid = formData.startShift && formData.endShift;
    if (!this.shiftsValid) {
      return;
    }
    const req = {
      empGuid: profile.empGuid,
      endTime: endDate,
      gender: Number(profile.gender) === 0 ? "Male" : "Female",
      officeName: profile.officeLocation,
      projectId: profile.projectId,
      startTime: date
    };
    this.multipleDaysSelected = multipleDays;
    const time = offsetInMins => {
      if (!offsetInMins) {
        return parseZone().tz(tz).startOf("day").valueOf().toString();
      }
      const dateStart = parseZone(date).tz(tz).startOf("day");
      return dateStart.add(offsetInMins, "minutes").valueOf().toString();
    };
    this.vehicleId = !!formData?.commute?.vehicleId ? formData?.commute?.vehicleId : "";
    let isHybridParking = "true";
    this.parkingFormOfficeGuid = formData.office.guid;
    this.parkingFormStartTime = time(formData.startShift?.timeInMinutes);
    this.parkingFormEndTime = time(formData.endShift?.timeInMinutes);
    this.setParkingStream(
      this.apiSvc.getParking(
        formData.office.guid,
        time(formData.startShift?.timeInMinutes),
        time(formData.endShift?.timeInMinutes),
        isHybridParking,
        this.getEntityTypeId()
      ),
      oldCommute.registrationNumber,
      configs.flattenedSettings.enableGridFloorPlan
    );
    formData = { ...formData, startDate: time(formData.startShift?.timeInMinutes), endDate: time(formData.endShift?.timeInMinutes) };
    this.gridUrlData$.next(formData);
    const res = await this.apiSvc.validateShift([req], formData.startShift?.shiftId || "", formData.endShift?.shiftId || "", tz).toPromise();
    this.loginOptions = await this.genCommuteOptions(
      configs,
      formData.startShift,
      res?.login?.[this.userSvc.employeeInfo.empGuid] || false,
      booking?.requestStatus
    );
    (this.logoutOptions = await this.genCommuteOptions(configs, formData.endShift, res?.logout?.[this.userSvc.employeeInfo.empGuid] || false)),
      booking?.requestStatus;
    const commuteValue = formData?.commute?.login;
    if (configs.flattenedSettings.enableWaitlistBooking && commuteValue?.id === "parkingWaitlist" && commuteValue?.value?.length) {
      localStorage.setItem("waitingPremiseIds", JSON.stringify(commuteValue?.value));
    }
    this.formData$.next([this.loginOptions, this.logoutOptions, oldCommute]);
  }

  removeLocalStorage() {
    localStorage.removeItem("waitingPremiseIds");
  }

  setEntityTypeId(entityTypeId: string) {
    this.entityTypeId = entityTypeId;
  }

  getEntityTypeId() {
    return this.entityTypeId;
  }

  clearFormData(): void {
    this.formData$ = new ReplaySubject<IFormData>(1);
    this.formData = this.formData$.asObservable();
  }

  setCommute(opt: [NsEmpWebExpData.ICommuteOption?, NsEmpWebExpData.ICommuteOption?, string?, string?, string?]): void {
    this.commuteChange$.next(opt);
  }

  private async genCommuteOptions(
    configs: NsWISConfigurations.IConfigurations,
    shift: NsEmpWebExpData.IShiftData,
    validity: boolean,
    statusOfBooking?: NsEmpWebExpData.IBookingStates
  ): Promise<NsEmpWebExpData.ICommuteOption[]> {
    return [
      {
        name: "Office Cab",
        id: "transport",
        disable: statusOfBooking === "SIGNED_IN" || !validity || !shift?.transportAvalability || !configs?.flattenedSettings.showCabs
      },
      {
        name: "None",
        disable: statusOfBooking === "SIGNED_IN",
        id: "none"
      }
    ];
  }

  setParkingStream(obs: Observable<NsEmpWebExpData.IParkingPremise[]>, regNo: string, parkingFloorPlan?: boolean): void {
    this.parkingRefreshStream$.next(obs);
    this.regNo.patchValue(regNo);
    this.enableGridFloorPlan = parkingFloorPlan;
  }

  setAllotedParkingData(data: NsEmpWebExpData.IAllotedParkingDetails) {
    this.allotedParkingDetails$.next(data);
  }

  removeWaitlistedParking(premiseId: string): void {
    this.removeCommute$.next(premiseId);
  }
}
